import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Card, CardContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, IconButton } from "@mui/material";
import { useDropzone } from "react-dropzone";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // Document Icon
import CloudUploadIcon from "@mui/icons-material/CloudUpload"; // Upload Icon
import DownloadIcon from "@mui/icons-material/Download"; // Download Icon
import AddIcon from "@mui/icons-material/Add"; // New Document Button
import { getLegalDocuments, uploadLegalDocument } from "../services/api"; // Backend API calls

const LegalCompliance = () => {
    const [documents, setDocuments] = useState([]);
    const [pendingSignatures, setPendingSignatures] = useState([]);

    useEffect(() => {
        fetchLegalDocuments();
    }, []);

    const fetchLegalDocuments = async () => {
        try {
            const { data } = await getLegalDocuments();
            setDocuments(data.documents);
            setPendingSignatures(data.pendingSignatures);
        } catch (error) {
            console.error("Error fetching legal documents", error);
        }
    };

    const onDrop = async (acceptedFiles) => {
        const file = acceptedFiles[0];
        const formData = new FormData();
        formData.append("document", file);

        try {
            await uploadLegalDocument(formData);
            fetchLegalDocuments(); // Refresh document list
        } catch (error) {
            console.error("Error uploading document", error);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: ".pdf,.doc,.docx" });

    return (
        <Box sx={{ padding: "20px" }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4" fontWeight="bold">Legal Document Management</Typography>
                    <Typography variant="body1" color="textSecondary">
                        Upload, manage and sign legal documents
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} size="small">New Document</Button>
            </Box>

            <Grid container spacing={3} sx={{ marginTop: 2 }}>
                {/* Upload Documents Section */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ padding: "20px", borderRadius: "10px" }}>
                        <Typography variant="h6" fontWeight="bold">Upload Documents</Typography>
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: "2px dashed #ccc",
                                padding: "30px",
                                textAlign: "center",
                                cursor: "pointer",
                                borderRadius: "10px",
                                marginTop: "15px"
                            }}
                        >
                            <input {...getInputProps()} />
                            <CloudUploadIcon sx={{ fontSize: 40, color: "#6e7864" }} />
                            <Typography variant="body1"><strong>Click to upload</strong> or drag and drop</Typography>
                            <Typography variant="body2" color="textSecondary">PDF, DOC, or DOCX (MAX. 10MB)</Typography>
                        </Box>
                    </Card>
                </Grid>

                {/* Pending Signatures Section */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ padding: "20px", borderRadius: "10px" }}>
                        <Typography variant="h6" fontWeight="bold">Pending Signatures</Typography>
                        {pendingSignatures.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" sx={{ marginTop: 2 }}>
                                No pending signatures.
                            </Typography>
                        ) : (
                            pendingSignatures.map((doc, index) => (
                                <Card key={index} sx={{ padding: "10px", marginTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Box display="flex" alignItems="center">
                                        <InsertDriveFileIcon sx={{ color: "#6e7864", marginRight: "10px" }} />
                                        <Box>
                                            <Typography variant="body1" fontWeight="bold">{doc.name}</Typography>
                                            <Typography variant="body2" color="textSecondary">Expires in {doc.expiresIn} days</Typography>
                                        </Box>
                                    </Box>
                                    <Button variant="outlined" size="small">Sign Now</Button>
                                </Card>
                            ))
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Documents Table */}
            <Box sx={{ marginTop: 4 }}>
                <Typography variant="h6" fontWeight="bold">Uploaded Documents</Typography>
                <TableContainer component={Paper} sx={{ marginTop: 2, borderRadius: "10px" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>Document Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Signatories</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documents.map((doc, index) => (
                                <TableRow key={index}>
                                    <TableCell>{doc.name}</TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            background: doc.status === "Completed" ? "#C8E6C9" : "#FFF3CD",
                                            padding: "5px 10px",
                                            borderRadius: "8px",
                                            display: "inline-block"
                                        }}>
                                            {doc.status}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{doc.date}</TableCell>
                                    <TableCell>
                                        {doc.signatories.map((sig, i) => (
                                            <Avatar key={i} src={sig.avatar} sx={{ width: 32, height: 32, marginRight: 1 }} />
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton>
                                            <DownloadIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default LegalCompliance;
